import React, { useState, useEffect } from 'react';
import { Search, Sparkles, TrendingUp, BarChart3, PieChart, LineChart, Loader2 } from 'lucide-react';
import { aiDashboardApi, ChartConfig } from '../../api/aiDashboardApi';
import { AdvancedChartRenderer } from './AdvancedChartRenderer';
import { D3AdvancedChart } from './D3AdvancedChart';
import { ChartRenderer } from './ChartRenderer';
// import { ChartLibrarySelector } from './ChartLibrarySelector';
import { SuggestedPrompts } from './SuggestedPrompts';

type ChartLibrary = 'recharts' | 'victory' | 'd3' | 'chartjs';

interface DashboardSettings {
  library: ChartLibrary;
  animated: boolean;
  interactive: boolean;
  theme: 'light' | 'dark';
  autoRefresh: boolean;
  refreshInterval: number;
}

export const EnhancedAIDashboard: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [charts, setCharts] = useState<ChartConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [settings] = useState<DashboardSettings>({
    library: 'recharts',
    animated: true,
    interactive: true,
    theme: 'light',
    autoRefresh: false,
    refreshInterval: 30
  });

  const filters = {
    dateRange: 'all',
    severity: 'all',
    status: 'all'
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (settings.autoRefresh && charts.length > 0) {
      interval = setInterval(() => {
        handleGenerateDashboard(true);
      }, settings.refreshInterval * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [settings.autoRefresh, settings.refreshInterval, charts.length]);

  const handleGenerateDashboard = async (isRefresh = false) => {
    if (!prompt.trim() && !isRefresh) return;

    setLoading(true);
    setError(null);
    if (!isRefresh) setShowSuggestions(false);

    try {
      const response = await aiDashboardApi.generateDashboard({
        prompt: isRefresh ? prompt : prompt,
        filters
      });
      setCharts(response.data.charts);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate dashboard');
      console.error('Dashboard generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePromptSelect = (selectedPrompt: string) => {
    setPrompt(selectedPrompt);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerateDashboard();
    }
  };



  const renderChart = (config: ChartConfig, index: number) => {
    const commonProps = {
      config,
      interactive: settings.interactive,
      animated: settings.animated
    };

    switch (settings.library) {
      case 'recharts':
      case 'victory':
        return (
          <AdvancedChartRenderer
            key={`${settings.library}-${index}`}
            {...commonProps}
            library={settings.library}
          />
        );
      case 'd3':
        return (
          <D3AdvancedChart
            key={`d3-${index}`}
            config={config}
            interactive={settings.interactive}
          />
        );
      case 'chartjs':
        return (
          <ChartRenderer
            key={`chartjs-${index}`}
            config={config}
          />
        );
      default:
        return (
          <AdvancedChartRenderer
            key={`default-${index}`}
            {...commonProps}
            library="recharts"
          />
        );
    }
  };

  return (
    <div className={`min-h-screen ${settings.theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} p-6`}>
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Advanced AI Dashboard
            </h1>
          </div>
          <p className={`${settings.theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-lg max-w-2xl mx-auto`}>
            Create stunning data visualizations with multiple chart libraries and advanced features
          </p>
        </div>





        {/* Search Input */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about your data... e.g., 'Show complaint trends over the last 3 months' or 'Which products have the most issues?'"
              className="w-full pl-12 pr-4 py-4 text-lg border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none shadow-sm"
              rows={3}
              disabled={loading}
            />
          </div>
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <BarChart3 className="h-4 w-4" />
              <span>Powered by {settings.library} • {settings.animated ? 'Animated' : 'Static'} • {settings.interactive ? 'Interactive' : 'Simple'}</span>
            </div>
            <button
              onClick={() => handleGenerateDashboard()}
              disabled={loading || !prompt.trim()}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4" />
                  Generate Dashboard
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <p className="text-red-700 font-medium">Error generating dashboard</p>
              </div>
              <p className="text-red-600 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Suggested Prompts */}
        {showSuggestions && !loading && charts.length === 0 && (
          <SuggestedPrompts onPromptSelect={handlePromptSelect} />
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin border-t-blue-500"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <PieChart className="h-6 w-6 text-blue-500" />
              </div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Analyzing your data with {settings.library}...</p>
            <p className="text-sm text-gray-500">Creating {settings.animated ? 'animated' : 'static'} {settings.interactive ? 'interactive' : ''} charts</p>
          </div>
        )}

        {/* Charts Grid */}
        {charts.length > 0 && !loading && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Generated Insights</h2>
                <p className="text-gray-600">
                  {charts.length} chart{charts.length !== 1 ? 's' : ''} • {settings.library} library
                  {settings.autoRefresh && (
                    <span className="ml-2 text-sm text-green-600">
                      • Auto-refreshing every {settings.refreshInterval}s
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => {
                  setCharts([]);
                  setPrompt('');
                  setShowSuggestions(true);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                New Query
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {charts.map((chart, index) => renderChart(chart, index))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {charts.length === 0 && !loading && !showSuggestions && (
          <div className="text-center py-16">
            <LineChart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No charts generated</h3>
            <p className="text-gray-500">Try asking a different question about your data</p>
          </div>
        )}
      </div>
    </div>
  );
};